import { StandardEvents } from '@shopify/events';
document.addEventListener('change', event => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement) || !select.matches('[data-gs-cart-variant]')) return;
  const form = select.closest('product-form-component');
  const input = form?.querySelector('input[ref="variantId"]');
  if (!input) return;
  input.value = select.value;
  const button = form.querySelector('button[ref="addToCartButton"]');
  if (button) button.disabled = !select.value || select.selectedOptions[0]?.disabled;
  const price = form.closest('article')?.querySelector('[data-gs-suggestion-price], [data-detail-price]');
  if (price && select.selectedOptions[0]?.dataset.price) price.textContent = select.selectedOptions[0].dataset.price;
});
document.addEventListener(StandardEvents.cartLinesUpdate, event => {
  if (event.action !== 'add') return;
  const modal = event.target instanceof Element ? event.target.closest('dialog.gs-stream__dialog') : null;
  if (!modal) return;
  Promise.resolve(event.promise).then(result => { if (!result?.detail?.didError && modal.open) modal.close(); }).catch(() => {});
});

class GoldenCartSuggestions extends HTMLElement {
  #controller;
  #gesture;
  #suppressClick = false;
  #clickTimer;

  connectedCallback() {
    this.#controller?.abort();
    const signal = (this.#controller = new AbortController()).signal;
    const grid = this.querySelector('.gs-cart-suggestions__grid');
    if (!grid) return;
    const cards = () => Array.from(grid.querySelectorAll('.gs-cart-suggestion'));
    const positions = () => {
      const items = cards();
      const first = items[0]?.offsetLeft ?? 0;
      const maximum = Math.max(0, grid.scrollWidth - grid.clientWidth);
      return [...new Set(items.map(card => Math.min(maximum, Math.max(0, card.offsetLeft - first))))];
    };
    const nearest = points => points.reduce((best, point, index) =>
      Math.abs(point - grid.scrollLeft) < Math.abs(points[best] - grid.scrollLeft) ? index : best, 0);
    const settle = (cancelled = false) => {
      const gesture = this.#gesture;
      if (!gesture) return;
      this.#gesture = undefined;
      grid.removeAttribute('data-dragging');
      if (!gesture.horizontal) return;
      const points = positions();
      const step = !cancelled && Math.abs(gesture.dx) >= 24 ? Math.sign(gesture.dx) : 0;
      const target = Math.max(0, Math.min(points.length - 1, gesture.index + step));
      grid.scrollTo({
        left: points[target] ?? 0,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
      });
      this.#suppressClick = true;
      clearTimeout(this.#clickTimer);
      this.#clickTimer = setTimeout(() => { this.#suppressClick = false; }, 400);
    };
    grid.addEventListener('touchstart', event => {
      if (event.touches.length !== 1) {
        settle(true);
        return;
      }
      if (event.target.closest('button,select,input,label')) return;
      const touch = event.touches[0];
      this.#gesture = {
        id: touch.identifier, x: touch.clientX, y: touch.clientY,
        start: grid.scrollLeft, index: nearest(positions()), dx: 0, horizontal: false
      };
    }, { passive: true, signal });
    grid.addEventListener('touchmove', event => {
      const gesture = this.#gesture;
      if (!gesture) return;
      if (event.touches.length !== 1) { settle(true); return; }
      const touch = Array.from(event.touches).find(item => item.identifier === gesture.id);
      if (!touch) return;
      const dx = gesture.x - touch.clientX;
      const dy = gesture.y - touch.clientY;
      if (!gesture.horizontal) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 8) return;
        if (Math.abs(dy) >= Math.abs(dx)) { this.#gesture = undefined; return; }
        gesture.horizontal = true;
        grid.setAttribute('data-dragging', '');
      }
      if (event.cancelable) event.preventDefault();
      gesture.dx = dx;
      const points = positions();
      const previous = points[Math.max(0, gesture.index - 1)] ?? 0;
      const next = points[Math.min(points.length - 1, gesture.index + 1)] ?? 0;
      grid.scrollLeft = Math.max(previous, Math.min(next, gesture.start + dx));
    }, { passive: false, signal });
    grid.addEventListener('touchend', () => settle(), { passive: true, signal });
    grid.addEventListener('touchcancel', () => settle(true), { passive: true, signal });
    grid.addEventListener('click', event => {
      if (!this.#suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      this.#suppressClick = false;
    }, { capture: true, signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#gesture = undefined;
    clearTimeout(this.#clickTimer);
    this.#suppressClick = false;
    this.querySelector('.gs-cart-suggestions__grid')?.removeAttribute('data-dragging');
  }
}

if (!customElements.get('golden-cart-suggestions')) {
  customElements.define('golden-cart-suggestions', GoldenCartSuggestions);
}
